import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import classNames from 'classnames'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Col, Container, Row } from 'reactstrap'
import Input from '../../../common/form/Input'
import Select from '../../../common/form/Select'
import Modal from '../../../common/Modal'
import SubmitButton from '../../../common/buttons/SubmitButton'
import FieldError from '../../../common/FieldError'
import MCElement from '../../../common/MCElement'
import ChildSocialFeatureSettings from '../../../common/ChildSocialFeatureSettings/ChildSocialFeatureSettings'
import AvatarPlaceholder from '../../../../assets/images/mock/default-profile-icon.png'
import { GENDER_OPTIONS, PASSWORD_REGEX, FAMILY_ADMIN_MIN_AGE } from '../../../../misc/Constants'
import { isValidEmail, isValidUsername, isValidPhone } from '../../../../utility/helperFunctions'
import CONSENTS from '../../../../constants/consentDocuments.json'
import Switcher from '../../../common/Switcher'

import styles from './AddFamilyMemberModal.module.scss'
import { useTranslated } from '../../../../i18n'
import { translateOptions } from '../../../../utility/i18nConverters'

const initialState = {
  firstName: '',
  lastName: '',
  birthdate: null,
  gender: null,
  familyAdmin: false,
  username: '',
  email: '',
  phone: '',
  phonePrefix: '',
  phonePrefixCountry: '',
  password: '',
  passwordConfirmation: '',
  avatar: null,
}

const initialSocialSettings = {
  allowPostingContent: true,
  allowPostingComments: true,
}

// eslint-disable-next-line max-lines-per-function, complexity
const AddFamilyMemberModal = ({ isOpen, onClose, handleCreate, loader }) => {
  const { t } = useTranslation()
  const currentUser = useSelector(state => state.user.current_user)
  const genderOptions = useTranslated(GENDER_OPTIONS, translateOptions)

  const [user, setUser] = useState(initialState)
  const [step, setStep] = useState(1)
  const [socialSettings, setSocialSettings] = useState(initialSocialSettings)
  const [isProxyConsentGiven, setProxyConsentGiven] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState('')
  const [errors, setErrors] = useState({})

  const variableData = {
    familyAdmin: {
      labelName: t('common.field.email'),
      inputAutocomplete: 'email',
      inputId: 'emailInput',
      inputName: 'email',
      placeholder: t('programs.registration.newFamilyMemberModal.placeholders.familyAdmin'),
      accountTypeTitle: t('programs.registration.newFamilyMemberModal.accountType.familyAdmin.title'),
      accountTypeDescription: t('programs.registration.newFamilyMemberModal.accountType.familyAdmin.description'),
    },
    student: {
      labelName: t('common.field.username'),
      inputAutocomplete: 'username',
      inputId: 'usernameInput',
      inputName: 'username',
      placeholder: t('programs.registration.newFamilyMemberModal.placeholders.student'),
      accountTypeTitle: t('programs.registration.newFamilyMemberModal.accountType.student.title'),
      accountTypeDescription: t('programs.registration.newFamilyMemberModal.accountType.student.description'),
    },
  }

  const customErrorMessage = useMemo(() => ({
    birthdateAdmins: t('programs.registration.newFamilyMemberModal.errors.birthdateAdmins', { count: FAMILY_ADMIN_MIN_AGE }),
    birthdateFuture: t('programs.registration.newFamilyMemberModal.errors.birthdateFuture'),
    username: t('programs.registration.newFamilyMemberModal.errors.username'),
    email: t('programs.registration.newFamilyMemberModal.errors.email'),
    phone: t('programs.registration.newFamilyMemberModal.errors.phone'),
    password: t('programs.registration.newFamilyMemberModal.errors.password'),
    unmatchingPasswords: t('programs.registration.newFamilyMemberModal.errors.unmatchingPasswords'),
  }), [t])

  const customData = user.familyAdmin ? variableData.familyAdmin : variableData.student
  const isUnderThirteen = user.birthdate && moment().diff(user.birthdate, 'years') < FAMILY_ADMIN_MIN_AGE
  const isInTheFuture = user.birthdate && moment().diff(user.birthdate, 'days') < 0
  const requiresSocialSettings = !user.familyAdmin && isUnderThirteen
  const isContinueStep = requiresSocialSettings && step === 1

  const isInvalidForm = useMemo(() => {
    if (user.familyAdmin) {
      return user.firstName === '' || user.lastName === '' || user.email === ''
    } else {
      return (
        user.firstName === ''
        || user.lastName === ''
        || user.birthdate === null
        || user.gender === null
        || user.username === ''
        || user.password === ''
        || user.passwordConfirmation === ''
      )
    }
  }, [user])

  const handleClose = () => {
    setUser(initialState)
    setStep(1)
    setSocialSettings(initialSocialSettings)
    setProxyConsentGiven(false)
    setAvatarUrl('')
    setErrors({})
    onClose()
  }

  const handleInputChange = (e) => {
    const inputName = e.target.name
    const inputValue = e.target.value

    setUser(prevState => ({
      ...prevState,
      [inputName]: inputValue,
    }))
  }

  const handleDateChange = (date) => {
    setUser(prevState => ({
      ...prevState,
      birthdate: date,
    }))
  }

  const handleGiveProxyConsent = () => {
    setProxyConsentGiven(prevState => !prevState)
  }

  const handleGenderSelect = (gender) => {
    setUser(prevState => ({
      ...prevState,
      gender,
    }))
  }

  const handleSetFamilyAdmin = () => {
    setUser(prevState => ({
      ...prevState,
      familyAdmin: !prevState.familyAdmin,
      username: '',
      email: '',
    }))
    setStep(1)
    setProxyConsentGiven(false)
    setErrors(prevState => ({
      ...prevState,
      username: null,
      email: null,
    }))
  }

  const handlePhoneChange = (phoneData) => {
    setUser(prevState => ({
      ...prevState,
      phone: phoneData.phone,
      phonePrefix: phoneData.phonePrefix,
      phonePrefixCountry: phoneData.phonePrefixCountry,
    }))
  }

  const handleAvatarChange = (event) => {
    if (event.target.value) {
      const file = event.target.files[0]
      setUser(prevState => ({
        ...prevState,
        avatar: file,
      }))
      setAvatarUrl(URL.createObjectURL(file))
    }
  }

  const validateBirthdate = useCallback(() => {
    if (isInTheFuture){
      setErrors(prevState => ({
        ...prevState, birthdate: [customErrorMessage.birthdateFuture],
      }))
      return false
    } else if (user.familyAdmin && isUnderThirteen) {
      setErrors(prevState => ({
        ...prevState, birthdate: [customErrorMessage.birthdateAdmins],
      }))
      return false
    } else {
      setErrors(prevState => ({
        ...prevState, birthdate: null,
      }))
      return true
    }
  }, [isInTheFuture, isUnderThirteen, user, customErrorMessage])

  useEffect(() => {
    validateBirthdate()
  }, [user, isUnderThirteen, isInTheFuture, validateBirthdate])

  const validateUsernameInput = username => {
    if (username && !isValidUsername(username)) {
      setErrors(prevState => ({
        ...prevState, username: [customErrorMessage.username],
      }))
      return false
    } else {
      setErrors(prevState => ({
        ...prevState, username: null,
      }))
      return true
    }
  }

  const validateEmailInput = email => {
    if (email && !isValidEmail(email)) {
      setErrors(prevState => ({
        ...prevState, email: [customErrorMessage.email],
      }))
      return false
    } else {
      setErrors(prevState => ({
        ...prevState, email: null,
      }))
      return true
    }
  }

  const validatePhoneNumber = phone => {
    if (phone && !isValidPhone(phone)) {
      setErrors(prevState => ({
        ...prevState, phone: [customErrorMessage.phone],
      }))
      return false
    } else {
      setErrors(prevState => ({
        ...prevState, phone: null,
      }))
      return true
    }
  }

  const validatePassword = password => {
    if (!password?.match(PASSWORD_REGEX)) {
      setErrors(prevState => ({
        ...prevState, password: [customErrorMessage.password],
      }))
      return false
    } else {
      setErrors(prevState => ({
        ...prevState, password: null,
      }))
      return true
    }
  }

  const validatePasswordsMatch = () => {
    if (user.password && user.passwordConfirmation && user.password !== user.passwordConfirmation) {
      setErrors(prevState => ({
        ...prevState, unmatchingPasswords: [customErrorMessage.unmatchingPasswords],
      }))
      return false
    } else {
      setErrors(prevState => ({
        ...prevState, unmatchingPasswords: null,
      }))
      return true
    }
  }

  const validateAllFields = () => {
    if (user.familyAdmin) {
      return validateEmailInput(user.email)
    } else {
      const validAge = validateBirthdate()
      const validUsername = validateUsernameInput(user.username)
      const validPhone = validatePhoneNumber(user.phone)
      const validPassword = validatePassword(user.password)
      const validMatchingPasswords = validatePasswordsMatch()

      return validAge && validUsername && validPhone && validPassword && validMatchingPasswords
    }
  }

  const handleContinue = () => {
    if (validateAllFields()) {
      setStep(2)
    }
  }

  const handleCreateMember = () => {
    if (!validateAllFields()) {
      return
    }

    const consent = {
      document_consented: 'coppa_policy',
      consented_on: new Date(),
      version: CONSENTS.coppa_policy.version,
      proxy_consent: true,
      proxy_consent_given_by: currentUser.id,
    }

    const { email, username, ...restUser } = { ...user }

    let userData

    if (user.familyAdmin) {
      userData = {
        firstName: user.firstName,
        lastName: user.lastName,
        email,
      }
    } else {
      userData = {
        ...restUser,
        username,
        birthdate: user.birthdate.format('YYYY-MM-DD'),
        gender: user.gender.value,
        familyId: currentUser.family_id,
        ...(requiresSocialSettings && {
          proxyConsentGiven: JSON.stringify(consent),
          userSettingAttributes: {
            allowPostingContent: socialSettings.allowPostingContent,
            allowPostingComments: socialSettings.allowPostingComments,
          },
        }),
      }
    }

    handleCreate(userData)
      .then(() => handleClose())
      .catch((errors) => {
        if (errors.status === 503) {
          toast.error(user.familyAdmin
            ? t('programs.registration.newFamilyMemberModal.errors.inviteUnavailable')
            : t('programs.registration.newFamilyMemberModal.errors.createJuniorUnavailable'))
        } else {
          setStep(1)
          setErrors(prevState => ({
            ...prevState,
            ...errors,
          }))
        }
      })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (requiresSocialSettings && step === 1) {
      handleContinue()
    } else {
      handleCreateMember()
    }
  }

  return (
    <Modal isOpen={isOpen} className={styles.newFamilyMemberModal} onClose={handleClose}>
      <Container fluid>
        <Row className='mb-3'>
          <Col xs={12} className='d-flex justify-content-center justify-content-sm-start align-items-center'>
            <h3 className={styles.newUserModalTitle}>{t('programs.registration.newFamilyMemberModal.title')}</h3>
          </Col>
        </Row>
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              <Row>
                <Col xs={12}>
                  <Switcher
                    isSwitched={user.familyAdmin}
                    onClick={handleSetFamilyAdmin}
                    options={[
                      t('programs.registration.newFamilyMemberModal.switcher.juniorGolfer'),
                      t('programs.registration.newFamilyMemberModal.switcher.adultGolfer'),
                    ]}
                  />
                </Col>
                <Col xs={12} className='my-3'>
                  <p className={styles.accountTypeTitle}>{customData.accountTypeTitle}</p>
                  <p className={styles.accountTypeDescription}>{customData.accountTypeDescription}</p>
                </Col>
              </Row>
              <Row className='mb-3'>
                <Col xs={12} sm={6} className='mb-3 mb-sm-0'>
                  <label htmlFor='firstNameInput'>{t('common.field.firstName')} <span className='required-text'>*</span></label>
                  <Input
                    id='firstNameInput'
                    type='text'
                    name='firstName'
                    autoComplete='given-name'
                    placeholder={t('programs.registration.newFamilyMemberModal.placeholders.firstName')}
                    value={user.firstName}
                    onChange={handleInputChange}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <label htmlFor='lastNameInput'>{t('common.field.lastName')} <span className='required-text'>*</span></label>
                  <Input
                    id='lastNameInput'
                    type='text'
                    name='lastName'
                    autoComplete='family-name'
                    placeholder={t('programs.registration.newFamilyMemberModal.placeholders.lastName')}
                    value={user.lastName}
                    onChange={handleInputChange}
                  />
                </Col>
              </Row>

              {!user.familyAdmin && (
                <Row className='mb-3'>
                  <Col xs={12} sm={6} className='mb-3 mb-sm-0'>
                    <label htmlFor='birthdateInput'>{t('common.field.birthdate')} <span className='required-text'>*</span></label>
                    <Input
                      id='birthdateInput'
                      type='date'
                      name='birthdate'
                      maxDate={new Date()}
                      placeholder='DD/MM/YYYY'
                      value={user.birthdate}
                      onChange={handleDateChange}
                      onBlur={validateBirthdate}
                      symbol={<i className='fa fa-calendar' />}
                      error={errors.birthdate}
                      autoComplete='off'
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={100}
                      dropdownMode='select'
                    />
                  </Col>
                  <Col xs={12} sm={6}>
                    <label htmlFor='genderSelect'>{t('common.field.gender')} <span className='required-text'>*</span></label>
                    <Select
                      id='genderSelect'
                      isSearchable={false}
                      placeholder={t('programs.registration.newFamilyMemberModal.placeholders.gender')}
                      value={user.gender}
                      onChange={handleGenderSelect}
                      options={genderOptions}
                    />
                  </Col>
                </Row>
              )}
              <Row className='mb-3'>
                <Col xs={12} sm={6} className='mb-3 mb-sm-0'>
                  <label htmlFor={customData.inputId}>
                    {customData.labelName} <span className='required-text'>*</span>
                  </label>
                  <Input
                    id={customData.inputId}
                    type='text'
                    name={customData.inputName}
                    autoComplete={user.familyAdmin ? 'email' : 'off'}
                    placeholder={customData.placeholder}
                    value={user.familyAdmin ? user.email : user.username}
                    onChange={handleInputChange}
                    onBlur={user.familyAdmin ? e => validateEmailInput(e.target.value) : e => validateUsernameInput(e.target.value)}
                    error={user.familyAdmin ? errors.email : errors.username}
                  />
                </Col>
                {!user.familyAdmin && (
                  <Col xs={12} sm={6}>
                    <label htmlFor='telephoneInput'>{t('common.field.phoneNumber')}</label>
                    <Input
                      id='telephoneInput'
                      type='telephone'
                      value={user.phone ? `${user.phonePrefix}${user.phone}` : user.phonePrefix || ''}
                      classNames={{ telInput: styles.telInput }}
                      onChange={handlePhoneChange}
                      onBLur={validatePhoneNumber}
                      error={errors.phone}
                    />
                  </Col>
                )}
              </Row>
              {!user.familyAdmin && (
                <>
                  <Row className='mb-4'>
                    <Col xs={12} sm={6} className='mb-3 mb-sm-0'>
                      <label htmlFor='passwordInput'>{t('common.field.password')} <span className='required-text'>*</span></label>
                      <Input
                        id='passwordInput'
                        type='password'
                        name='password'
                        autoComplete='new-password'
                        placeholder={t('common.field.password')}
                        value={user.password}
                        onChange={handleInputChange}
                        onBlur={e => {
                          validatePassword(e.target.value)
                          validatePasswordsMatch()
                        }}
                        error={errors.password}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <label htmlFor='confirmPasswordInput'>{t('common.field.confirmPassword')} <span className='required-text'>*</span></label>
                      <Input
                        id='confirmPasswordInput'
                        type='password'
                        name='passwordConfirmation'
                        autoComplete='new-password'
                        placeholder={t('common.field.confirmPassword')}
                        value={user.passwordConfirmation}
                        onChange={handleInputChange}
                        onBlur={validatePasswordsMatch}
                      />
                    </Col>
                    {errors.unmatchingPasswords && (
                      <Col xs={12}>
                        <FieldError message={errors.unmatchingPasswords} />
                      </Col>
                    )}
                  </Row>
                  <Row className='mb-3'>
                    <Col xs={12} sm={6}>
                      <input id='avatarInput' type='file' accept='image/*' className='d-none' onChange={handleAvatarChange} />
                      <label htmlFor='avatarInput' className={classNames(styles.uploadedAvatar, 'd-flex align-items-center')}>
                        <div className={classNames('small-round-image-wrapper', styles['dark-navy-border'])}>
                          <img
                            className='round-image'
                            src={avatarUrl || AvatarPlaceholder}
                            alt={t('programs.registration.newFamilyMemberModal.avatarAlt')}
                          />
                        </div>
                        <span className='mx-2'><i className='fa fa-plus' /></span>
                        <span>{t('programs.registration.newFamilyMemberModal.addPhoto')}</span>
                      </label>
                    </Col>
                    {errors.photo && (
                      <Col xs={12}>
                        <FieldError message={errors.photo} />
                      </Col>
                    )}
                  </Row>
                </>
              )}
              <p className={styles.requiredFieldsInfo}>{t('programs.registration.newFamilyMemberModal.requiredFieldsInfo')}</p>
            </>
          )}

          {step === 2 && (
            <div className='mt-5 mb-3'>
              <ChildSocialFeatureSettings
                stacked
                allowPostingContent={socialSettings.allowPostingContent}
                allowPostingComments={socialSettings.allowPostingComments}
                onChangePostingContent={(checked) => setSocialSettings(prev => ({ ...prev, allowPostingContent: checked }))}
                onChangePostingComments={(checked) => setSocialSettings(prev => ({ ...prev, allowPostingComments: checked }))}
              />

              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div
                className={classNames('mt-2 mb-4 d-flex', styles.proxyConsent)}
                onClick={() => handleGiveProxyConsent()}
              >
                <MCElement type='checkbox' isSelected={isProxyConsentGiven} onKeyDown={e => { e.key === 'Enter' && handleGiveProxyConsent() }}/>
                <span className='ml-2'>{t('programs.registration.newFamilyMemberModal.guardianConsent')}</span>
              </div>
            </div>
          )}

          <div className='h-100 flex-column-reverse flex-sm-row justify-content-center align-items-center'>
            <SubmitButton
              type='button'
              noTopMargin
              className='mb-3'
              buttonMessage={isContinueStep
                ? t('common.action.continue')
                : (user.familyAdmin
                  ? t('programs.registration.newFamilyMemberModal.sendInvite')
                  : t('programs.registration.newFamilyMemberModal.createMember'))}
              loading={!isContinueStep && loader}
              disabled={isContinueStep ? isInvalidForm : (loader || isInvalidForm || (step === 2 && !isProxyConsentGiven))}
              onClick={isContinueStep ? handleContinue : handleCreateMember}
            />
            <SubmitButton
              type='button'
              noTopMargin
              buttonMessage={step === 2 ? t('common.flow.back') : t('common.cancel')}
              buttonColor='navy-button'
              onClick={step === 2 ? () => setStep(1) : handleClose}
            />
          </div>
        </form>
      </Container>
    </Modal>
  )
}

AddFamilyMemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleCreate: PropTypes.func.isRequired,
  loader: PropTypes.bool,
}

export default AddFamilyMemberModal
